import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Copy, ExternalLink, Github, Loader2, RefreshCw, AlertTriangle, Cpu,
  Terminal, Download, Link2, Wrench, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import { apiPath } from '../lib/http';

const REPO_URL = 'https://github.com/SFARPak/AliFullStack';
const RELEASE_URL = 'https://github.com/SFARPak/AliFullStack/releases/latest';

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

const STATUS_LABELS = {
  idle: 'Connecting…',
  cloning: 'Preparing environment…',
  installing: 'Preparing environment…',
  starting: 'Starting dashboard…',
  running: 'Running',
  error: 'Error',
};

function buildViewUrl(status) {
  if (!status?.ready) return null;
  const token = localStorage.getItem('token') || '';
  const previewPath = status.preview_port_path;
  if (previewPath && typeof window !== 'undefined') {
    return `${window.location.origin}${previewPath}`;
  }
  const base = apiPath('/labs/alifullstack/view');
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${base}${qs}`;
}

const AliFullStackLab = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const suggestedPrompt = useMemo(() => {
    const raw = searchParams.get('prompt') || searchParams.get('q') || '';
    return raw.trim();
  }, [searchParams]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [apiError, setApiError] = useState('');
  const [iframeLoading, setIframeLoading] = useState(true);
  const [attachPort, setAttachPort] = useState('5173');
  const [showAlternatives, setShowAlternatives] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(apiPath('/labs/alifullstack/status'), {
        headers: authHeaders(),
      });
      if (res.status === 404) {
        setApiError('Snag Builder lab API not found — restart the backend (port 8002).');
        return null;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to load status (${res.status})`);
      }
      const data = await res.json();
      setStatus(data);
      if (data.deps_broken) setShowAlternatives(true);
      setApiError('');
      return data;
    } catch (err) {
      const msg = err.message || 'Unable to reach Snag Builder lab';
      setApiError(msg.includes('Failed to fetch')
        ? 'Cannot reach backend. Is it running on port 8002? (Frontend uses /api proxy in dev.)'
        : msg);
      setShowAlternatives(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postLabAction = useCallback(async (path, body = {}) => {
    setActionPending(true);
    setApiError('');
    try {
      const res = await fetch(apiPath(path), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setApiError('Snag Builder lab API not found — restart the backend on port 8002.');
        return null;
      }
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
      }
      setStatus(data);
      return data;
    } catch (err) {
      const msg = err.message || 'Request failed';
      setApiError(msg);
      toast.error(msg);
      return null;
    } finally {
      setActionPending(false);
    }
  }, []);

  const ensureLab = useCallback(async (forceRestart = false) => {
    const data = await postLabAction('/labs/alifullstack/ensure', { force_restart: forceRestart });
    if (data?.ready) toast.success('Snag Builder dashboard is ready');
    else if (data?.desktop_ready) toast.success('Snag Builder opened — check your taskbar');
    else if (data?.status === 'error') toast.error(data.message || 'Snag Builder failed to start');
    return data;
  }, [postLabAction]);

  const repairVendor = useCallback(async () => {
    toast.info('Repairing vendor (pnpm reinstall) — one-time, several minutes…');
    const data = await postLabAction('/labs/alifullstack/repair');
    if (data && !data.deps_broken && data.deps_installed) {
      toast.success('Vendor repaired — starting…');
      await ensureLab(false);
    }
    return data;
  }, [ensureLab, postLabAction]);

  const attachPortAction = useCallback(async () => {
    const port = parseInt(attachPort, 10);
    if (!Number.isFinite(port)) {
      toast.error('Enter a valid port number');
      return null;
    }
    const data = await postLabAction('/labs/alifullstack/attach', { port });
    if (data?.ready) toast.success(`Connected to port ${port}`);
    return data;
  }, [attachPort, postLabAction]);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const current = await fetchStatus();
      if (cancelled) return;
      if (!current?.ready && current?.env?.ready_to_run && !current?.deps_broken) {
        await ensureLab(false);
      }
    };

    boot();

    pollRef.current = setInterval(() => {
      fetchStatus();
    }, 4000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ensureLab, fetchStatus]);

  const isBusy = ['cloning', 'installing', 'starting'].includes(status?.status) || actionPending || status?.prewarming;
  const desktopMode = Boolean(status?.desktop_ready || status?.desktop_mode);
  const iframeUrl = buildViewUrl(status);

  useEffect(() => {
    if (iframeUrl) setIframeLoading(true);
  }, [iframeUrl]);

  const copyPrompt = () => {
    const text = suggestedPrompt || 'Build a grocery store landing page with hero, features, testimonials, and checkout CTA.';
    navigator.clipboard.writeText(text).then(() => toast.success('Prompt copied — paste it in Snag Builder below'));
  };

  const copyManualCommands = () => {
    const lines = status?.manual_start?.length
      ? status.manual_start
      : [
        'cd backend/vendor/alifullstack',
        'corepack enable',
        'pnpm install',
        'pnpm start',
      ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => toast.success('Commands copied — paste in PowerShell or Terminal'));
  };

  const alternativesPanel = (
    <div className="mx-auto mt-6 max-w-xl rounded-xl border border-white/[0.08] bg-white/[0.02] p-5 text-left">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">Alternative ways to start</p>
      <div className="space-y-3">
        {status?.deps_broken ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3">
            <p className="text-[11px] font-medium text-amber-100">Broken install detected (npm stubs, missing pnpm packages)</p>
            <button
              type="button"
              disabled={isBusy}
              onClick={repairVendor}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600/80 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-500 disabled:opacity-50"
            >
              <Wrench className="h-3.5 w-3.5" />
              Repair vendor (pnpm reinstall)
            </button>
          </div>
        ) : null}

        <div className="rounded-lg border border-white/[0.06] p-3">
          <p className="text-[11px] font-medium text-zinc-200">1. Manual terminal (most reliable)</p>
          <p className="mt-1 text-[10px] text-zinc-500">Run Snag Builder yourself, then connect below.</p>
          <button
            type="button"
            onClick={copyManualCommands}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] text-zinc-200 hover:bg-white/10"
          >
            <Terminal className="h-3 w-3" />
            Copy commands
          </button>
        </div>

        <div className="rounded-lg border border-white/[0.06] p-3">
          <p className="text-[11px] font-medium text-zinc-200">2. Connect to running instance</p>
          <p className="mt-1 text-[10px] text-zinc-500">After <code className="text-zinc-300">pnpm start</code>, enter the Vite port from the terminal (often 5173).</p>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={attachPort}
              onChange={(e) => setAttachPort(e.target.value)}
              className="w-24 rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-[11px] text-white"
              placeholder="5173"
            />
            <button
              type="button"
              disabled={isBusy}
              onClick={attachPortAction}
              className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
            >
              <Link2 className="h-3 w-3" />
              Connect
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-white/[0.06] p-3">
          <p className="text-[11px] font-medium text-zinc-200">3. Desktop app (no browser embed)</p>
          <p className="mt-1 text-[10px] text-zinc-500">Download the native Electron build from GitHub releases.</p>
          <button
            type="button"
            onClick={() => window.open(RELEASE_URL, '_blank', 'noopener,noreferrer')}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] text-zinc-200 hover:bg-white/10"
          >
            <Download className="h-3 w-3" />
            GitHub releases
          </button>
        </div>

        <div className="rounded-lg border border-white/[0.06] p-3">
          <p className="text-[11px] font-medium text-zinc-200">4. Use Snagcart App Builder instead</p>
          <p className="mt-1 text-[10px] text-zinc-500">Built-in SaaS builder — no Electron, works in browser today.</p>
          <button
            type="button"
            onClick={() => navigate('/snagcart')}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-violet-500/30 bg-violet-500/10 px-2.5 py-1.5 text-[10px] text-violet-200 hover:bg-violet-500/20"
          >
            <Sparkles className="h-3 w-3" />
            Open App Builder
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardShell
      user={user}
      title="Snag Builder"
      subtitle="Local-first AI app builder — powered by Snagcart"
      activeNav="websites"
      fullscreen
      backPath="/websites"
      headerExtras={(
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAlternatives((v) => !v)}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-zinc-300 hover:bg-white/[0.06]"
          >
            <Terminal className="h-3.5 w-3.5" />
            Alternatives
          </button>
          <button
            type="button"
            onClick={() => window.open(REPO_URL, '_blank', 'noopener,noreferrer')}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-zinc-300 hover:bg-white/[0.06]"
          >
            <Github className="h-3.5 w-3.5" />
            GitHub
          </button>
          {iframeUrl ? (
            <button
              type="button"
              onClick={() => window.open(iframeUrl, '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5 text-[11px] text-zinc-300 hover:bg-white/[0.06]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open tab
            </button>
          ) : null}
          <button
            type="button"
            disabled={isBusy}
            onClick={() => ensureLab(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-1.5 text-[11px] text-violet-200 hover:bg-violet-500/20 disabled:opacity-50"
            data-testid="alifullstack-lab-restart"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isBusy ? 'animate-spin' : ''}`} />
            Restart
          </button>
        </div>
      )}
    >
      <div className="flex h-full min-h-0 flex-col bg-[#080808]" data-testid="alifullstack-lab">
        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-zinc-400">
            <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            <p className="text-sm">Checking Snag Builder lab…</p>
          </div>
        ) : apiError && !status ? (
          <div className="mx-auto mt-16 max-w-lg rounded-xl border border-red-500/30 bg-red-500/[0.06] p-6">
            <div className="mb-3 flex items-center gap-2 text-red-200">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-sm font-semibold">Cannot reach Snag Builder lab</h2>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-zinc-400">{apiError}</p>
            <button
              type="button"
              onClick={() => { setLoading(true); fetchStatus(); }}
              className="mr-3 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate('/websites')}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to My Websites
            </button>
            {showAlternatives ? alternativesPanel : null}
          </div>
        ) : desktopMode ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 py-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/20 ring-1 ring-emerald-500/40">
              <Cpu className="h-8 w-8 text-emerald-300" />
            </div>
            <div className="max-w-lg">
              <h2 className="text-base font-semibold text-white">Snag Builder is running on your desktop</h2>
              <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                Snag Builder is an <strong className="font-medium text-zinc-200">Electron desktop app</strong>, not a
                web page — it cannot load inside this browser tab. Look for the
                {' '}
                <strong className="font-medium text-zinc-200">Snag Builder</strong>
                {' '}
                window on your taskbar (Alt+Tab on Windows).
              </p>
              <p className="mt-2 text-[11px] text-zinc-500">
                First compile takes 2–4 minutes; the window may appear behind other apps.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => ensureLab(true)}
                className="rounded-lg border border-white/10 px-4 py-2 text-xs text-zinc-300 hover:bg-white/5"
              >
                Restart Snag Builder
              </button>
              <button
                type="button"
                onClick={() => navigate('/snagcart')}
                className="rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500"
              >
                Use App Builder (browser)
              </button>
            </div>
            {alternativesPanel}
          </div>
        ) : !iframeUrl ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center overflow-y-auto py-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600">
              <Cpu className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">
                {status?.status === 'error'
                  ? 'Snag Builder failed to start'
                  : (STATUS_LABELS[status?.status] || 'Preparing Snag Builder')}
              </p>
              <p className="mt-1 max-w-md text-xs text-zinc-500">
                {status?.status === 'error'
                  ? (status?.message || 'See alternatives below.')
                  : (status?.prewarming || isBusy
                    ? 'First start downloads Electron and compiles — can take 2–4 minutes.'
                    : (status?.message || 'Shared SaaS instance — one dashboard for all users on this server.'))}
              </p>
              {status?.deps_broken ? (
                <p className="mt-2 text-[11px] text-amber-300/90">
                  Install is broken (used npm instead of pnpm). Use Repair or manual start below.
                </p>
              ) : null}
            </div>
            {isBusy ? (
              <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => ensureLab(status?.status === 'error')}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500"
                  data-testid="alifullstack-lab-start"
                >
                  {status?.status === 'error' ? 'Retry start' : 'Auto start'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAlternatives(true)}
                  className="rounded-lg border border-white/10 px-4 py-2 text-xs text-zinc-300 hover:bg-white/5"
                >
                  Other ways to start
                </button>
              </div>
            )}
            {(showAlternatives || status?.status === 'error') ? alternativesPanel : null}
          </div>
        ) : (
          <>
            <div className="shrink-0 border-b border-violet-500/20 bg-violet-500/[0.06] px-4 py-2.5">
              <p className="text-[11px] text-violet-100/90">
                <strong className="font-semibold">Snag Builder dashboard</strong>
                {' '}
                — {status?.start_mode === 'attach' ? 'connected to your local instance' : 'shared SaaS instance'}.
              </p>
              {suggestedPrompt ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="max-w-full truncate rounded bg-black/30 px-2 py-1 text-[10px] text-zinc-200">
                    {suggestedPrompt}
                  </code>
                  <button
                    type="button"
                    onClick={copyPrompt}
                    className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-zinc-200 hover:bg-white/10"
                  >
                    <Copy className="h-3 w-3" />
                    Copy prompt
                  </button>
                </div>
              ) : null}
            </div>
            <div className="relative min-h-0 flex-1">
              {iframeLoading ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#080808]">
                  <Loader2 className="h-7 w-7 animate-spin text-violet-400" />
                  <p className="text-xs text-zinc-400">Loading Snag Builder UI… first compile can take 1–3 minutes</p>
                  <button
                    type="button"
                    onClick={() => window.open(iframeUrl, '_blank', 'noopener,noreferrer')}
                    className="text-[11px] text-violet-300 underline-offset-2 hover:underline"
                  >
                    Open in new tab if still blank
                  </button>
                </div>
              ) : null}
              <iframe
                title="Snag Builder Lab"
                src={iframeUrl}
                className="h-full w-full min-h-[480px] border-0 bg-[#0a0a0a]"
                data-testid="alifullstack-lab-iframe"
                allow="clipboard-read; clipboard-write"
                onLoad={() => setIframeLoading(false)}
              />
            </div>
          </>
        )}

        {status?.status === 'error' && status?.message ? (
          <div className="border-t border-red-500/20 bg-red-500/[0.06] px-4 py-2 text-[11px] text-red-200">
            {status.message}
            <button
              type="button"
              onClick={() => setShowAlternatives(true)}
              className="ml-3 underline underline-offset-2 hover:text-red-100"
            >
              Show alternatives
            </button>
          </div>
        ) : null}
      </div>
    </DashboardShell>
  );
};

export default AliFullStackLab;
